/**
 * OrgUnitNode — kafelek jednostki organizacyjnej w organigramie (React Flow custom node),
 * F24 Pakiet 0.
 *
 * Organigram jest teraz zbudowany z DRZEWA JEDNOSTEK (`/org-units/tree`), nie z płaskiej
 * listy pracowników po `managerEmployeeId` — przełożony wynika dziś z kierownika jednostki
 * (z ewentualnym nadpisaniem na karcie pracownika), więc klient nie potrafi już wiarygodnie
 * zrekonstruować hierarchii z samych rekordów kadrowych. Ten węzeł reprezentuje SAMĄ
 * jednostkę (nazwa + rodzaj + kierownik); pracownicy przypisani do niej wiszą pod nią jako
 * osobne węzły `employee` (patrz `OrgChartCanvas.tsx`).
 *
 * Handle: target góra (jednostka nadrzędna) — brak dla korzenia drzewa (`isRoot`). Source
 * dół — zawsze (dzieci: podjednostki + pracownicy).
 */

import { memo } from 'react';
import { Handle, type NodeProps, Position } from '@xyflow/react';

import type { OrgUnitKind } from '@/lib/use-tenant-people';

export interface OrgUnitNodeData {
  name: string;
  kind: OrgUnitKind;
  kindLabel: string;
  managerName: string | null;
  /** Węzeł-korzeń drzewa jednostek tej firmy — inne obramowanie + brak handle'a górnego. */
  isRoot: boolean;
  [key: string]: unknown;
}

const KIND_ACCENT: Record<OrgUnitKind, string> = {
  department: 'border-blue-300 dark:border-blue-700',
  division: 'border-purple-300 dark:border-purple-700',
  branch: 'border-amber-300 dark:border-amber-700',
};

export const OrgUnitNode = memo(function OrgUnitNode({ data }: NodeProps) {
  const d = (data as OrgUnitNodeData) ?? ({} as OrgUnitNodeData);

  return (
    <div
      className={`w-[220px] overflow-hidden rounded-lg border-2 bg-bg-elevated shadow-md ${
        d.isRoot ? 'border-accent-500' : (KIND_ACCENT[d.kind] ?? '')
      }`}
      data-testid="org-node-unit"
    >
      {!d.isRoot && (
        <Handle
          type="target"
          position={Position.Top}
          className="!h-2.5 !w-2.5 !border-2 !border-bg-elevated !bg-border-primary"
        />
      )}
      <div className="space-y-1 px-3 py-2.5">
        <div className="truncate text-sm font-semibold text-text-primary">{d.name}</div>
        <div className="flex items-center justify-between gap-2">
          <span className="inline-block max-w-full truncate rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
            {d.kindLabel}
          </span>
        </div>
        {d.managerName && (
          <div className="truncate text-xs text-text-tertiary">{d.managerName}</div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-2.5 !w-2.5 !border-2 !border-bg-elevated !bg-border-primary"
      />
    </div>
  );
});
